import { useAuth0 } from "@auth0/auth0-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useViewPermission } from "../context/PermissionContext";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "./Navbar";
import NotificationContext from "../context/NotificationContext";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Loader from "./Loader";
import AccessDenied from "./AccessDenied";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  loadPdfBrandAssets,
  PDF_BRAND_FOOTER_MARGIN,
  pdfBrandDidDrawPage,
} from "../utils/pdfBrand";
import { generateCourseFeePeriods } from "../utils/feePeriods";
import { formatDisplayDate } from "../utils/dateFormat";
import PaginationFooter from "./PaginationFooter";
import AppTooltip from "./AppTooltip";
import {
  DEFAULT_PAGE_SIZE,
  exportListBody,
  normalizeListResponse,
} from "../utils/listPagination";
import * as teachersApi from "../api/teachers";
import * as coursesApi from "../api/courses";
import * as registrationsApi from "../api/registrations";

const thStyle = {
  backgroundColor: "#d5d1fd",
  color: "#403b55",
  fontWeight: "600",
  padding: "14px 16px",
  textAlign: "center",
  letterSpacing: "0.4px",
  fontSize: "13px",
  borderBottom: "1px solid #e6e3eb",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

const tdStyle = {
  padding: "14px 16px",
  textAlign: "center",
  fontSize: "13px",
  color: "#403b55",
  borderBottom: "1px solid #f0eef4",
};

const controlBtnStyle = {
  height: "38px",
  backgroundColor: "#fff",
  color: "#403b55",
  border: "none",
  padding: "0 15px",
  borderRadius: "8px",
  fontWeight: "600",
  fontSize: "13px",
  whiteSpace: "nowrap",
};

function TeacherDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_teachers");
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);

  const [courses, setCourses] = useState([]);
  const [showActiveCoursesOnly, setShowActiveCoursesOnly] = useState(true);

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [appliedFilter, setAppliedFilter] = useState(null);

  const [viewMode, setViewMode] = useState("empty");
  const [courseRows, setCourseRows] = useState([]);
  const [monthRows, setMonthRows] = useState([]);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = DEFAULT_PAGE_SIZE;

  useEffect(() => {
    if (permLoading || !hasPermission || !id) return;

    let cancelled = false;
    const fetchTeacher = async () => {
      try {
        const response = await teachersApi.get(id);
        if (!cancelled) {
          setTeacher(response.data);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching teacher:", err);
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTeacher();
    return () => {
      cancelled = true;
    };
  }, [id, hasPermission, permLoading]);

  useEffect(() => {
    setViewMode("empty");
    setCourseRows([]);
    setMonthRows([]);
    setAppliedFilter(null);
    setSelectedCourse(null);
    setSelectedMonth(null);
  }, [teacher?.id]);

  useEffect(() => {
    if (permLoading || !hasPermission || !teacher) return;

    let cancelled = false;

    const fetchCourses = async () => {
      setLoading(true);
      try {
        const response = await coursesApi.list(
          exportListBody({ teacherId: teacher.id }),
        );
        if (!cancelled) {
          setCourses(normalizeListResponse(response.data).data || []);
        }
      } catch (err) {
        console.error("Error fetching courses:", err);
        if (!cancelled) {
          setCourses([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCourses();
    return () => {
      cancelled = true;
    };
  }, [teacher, hasPermission, permLoading]);

  const listedCourses = useMemo(
    () =>
      (courses || []).filter(
        (course) => !showActiveCoursesOnly || Number(course.active) === 1,
      ),
    [courses, showActiveCoursesOnly],
  );

  const courseMonths = useMemo(() => {
    if (!selectedCourse) return [];
    return generateCourseFeePeriods(selectedCourse);
  }, [selectedCourse]);

  const fetchPaidPeriods = async (payload) => {
    const response = await registrationsApi.teacherPaidPeriods(payload);
    return response.data || [];
  };

  const resetToEmpty = () => {
    setViewMode("empty");
    setCourseRows([]);
    setMonthRows([]);
    setAppliedFilter(null);
    setSelectedCourse(null);
    setSelectedMonth(null);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (
      selectedCourse &&
      !listedCourses.some((course) => course.id === selectedCourse.id)
    ) {
      resetToEmpty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listedCourses, selectedCourse]);

  const handleToggleActiveCourses = () => {
    setShowActiveCoursesOnly((prev) => !prev);
    resetToEmpty();
  };

  const handleSelectCourse = async (course) => {
    if (selectedCourse?.id === course.id) {
      resetToEmpty();
      return;
    }

    setSelectedCourse(course);
    setSelectedMonth(null);
    setAppliedFilter(null);
    setMonthRows([]);
    setCurrentPage(1);
    setLoadingSheet(true);
    setViewMode("course");

    try {
      const rows = await fetchPaidPeriods({
        teacherId: teacher.id,
        courseId: course.id,
      });
      setCourseRows(rows);
    } catch (err) {
      console.error("Error fetching course paid periods:", err);
      notificationCtx.show({
        message: "Failed to load paid registrations.",
        stat: "error",
      });
      setCourseRows([]);
    } finally {
      setLoadingSheet(false);
    }
  };

  const handleApplyFilter = async () => {
    if (!selectedCourse || !selectedMonth) {
      notificationCtx.show({
        message: "Select a course and a month before applying the filter.",
      });
      return;
    }

    const nextFilter = {
      courseId: selectedCourse.id,
      courseTitle: selectedCourse.title,
      month: selectedMonth.month,
      year: selectedMonth.year,
      periodLabel: selectedMonth.label,
    };

    setLoadingSheet(true);
    try {
      const rows = await fetchPaidPeriods({
        teacherId: teacher.id,
        courseId: nextFilter.courseId,
        month: nextFilter.month,
        year: nextFilter.year,
      });
      setMonthRows(rows);
      setAppliedFilter(nextFilter);
      setViewMode("month");
      setCurrentPage(1);
    } catch (err) {
      console.error("Error fetching filtered teacher paid periods:", err);
      notificationCtx.show({
        message: "Failed to load paid registrations.",
        stat: "error",
      });
      setMonthRows([]);
    } finally {
      setLoadingSheet(false);
    }
  };

  const handleClearFilter = () => {
    resetToEmpty();
  };

  const teacherShareForAmount = (amountPaid) => {
    const commission = Number(teacher?.commission || 0);
    return Math.round(Number(amountPaid || 0) * ((100 - commission) / 100));
  };

  const teacherShareForRow = (row) => teacherShareForAmount(row.amountPaid);

  const feePeriodStatusLabel = (status) => {
    if (status === "waived") return "Not Applicable";
    if (status === "zero_due") return "FOC";
    if (status === "paid") return "Paid";
    return status || "—";
  };

  const matrixStudents = useMemo(() => {
    const byRegistration = new Map();
    for (const row of courseRows) {
      const key = row.registrationId;
      if (!byRegistration.has(key)) {
        byRegistration.set(key, {
          registrationId: key,
          name: row.name || "",
          email: row.email || "",
          feemode: row.feemode || "",
          discount: row.discount || 0,
          periods: {},
        });
      }
      const student = byRegistration.get(key);
      student.periods[`${row.year}-${row.month}`] = row;
      if (Number(row.discount) > Number(student.discount)) {
        student.discount = row.discount;
      }
    }
    return [...byRegistration.values()].sort((a, b) =>
      String(a.name).localeCompare(String(b.name)),
    );
  }, [courseRows]);

  const courseShareTotal = useMemo(() => {
    let total = 0;
    for (const student of matrixStudents) {
      for (const period of courseMonths) {
        const cell = student.periods[`${period.year}-${period.month}`];
        if (cell) total += teacherShareForRow(cell);
      }
    }
    return total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixStudents, courseMonths, teacher?.commission]);

  const monthShareTotal = useMemo(
    () => monthRows.reduce((sum, row) => sum + teacherShareForRow(row), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthRows, teacher?.commission],
  );

  const tableRows =
    viewMode === "month"
      ? monthRows
      : viewMode === "course"
        ? matrixStudents
        : [];
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const paginatedRows = tableRows.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.max(1, Math.ceil(tableRows.length / rowsPerPage) || 0);
  const rowLabel = viewMode === "month" ? "registrations" : "students";

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const canExport =
    (viewMode === "course" && matrixStudents.length > 0) ||
    (viewMode === "month" && monthRows.length > 0);

  const renderMonthCell = (periodRow) => {
    if (!periodRow) return "—";

    const status = periodRow.feePeriodStatus;
    if (status === "waived" || status === "zero_due") {
      const share = teacherShareForRow(periodRow);
      if (share <= 0) {
        return (
          <span
            style={{
              display: "inline-block",
              padding: "4px 7px",
              borderRadius: "6px",
              backgroundColor: status === "waived" ? "#f9e1be" : "#f3f1f7",
              color: status === "waived" ? "#8a5a00" : "#625d75",
              fontSize: "10px",
              fontWeight: "600",
            }}
          >
            {feePeriodStatusLabel(status)}
          </span>
        );
      }
    }

    const share = teacherShareForRow(periodRow);
    return (
      <div style={{ lineHeight: "1.5" }}>
        <div style={{ fontWeight: "600" }}>PKR {share.toLocaleString()}</div>
        <div style={{ fontSize: "11px", color: "#8a8498" }}>
          {formatDisplayDate(periodRow.paymentDate)}
        </div>
      </div>
    );
  };

  const downloadPDF = async () => {
    if (!teacher || !selectedCourse) return;
    if (viewMode === "course" && matrixStudents.length === 0) return;
    if (viewMode === "month" && monthRows.length === 0) return;

    const doc = new jsPDF({ orientation: "landscape" });
    const marginX = 14;
    const assets = await loadPdfBrandAssets(doc);
    const tableTopMargin = assets.headerH + 6;
    const brandPageHook = pdfBrandDidDrawPage(
      doc,
      assets,
      "teacher fee reports",
      marginX,
    );

    const commission = Number(teacher.commission || 0);
    let cursorY = assets.headerH + 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(48, 45, 69);
    doc.text(`${teacher.name}`, marginX, cursorY);
    cursorY += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(64, 59, 85);
    if (viewMode === "month" && appliedFilter) {
      doc.text(
        `${selectedCourse.title} · ${appliedFilter.periodLabel}`,
        marginX,
        cursorY,
      );
    } else {
      doc.text(`${selectedCourse.title}`, marginX, cursorY);
    }
    cursorY += 6;
    doc.text(`Commission ${commission}%`, marginX, cursorY);
    cursorY += 6;

    const total =
      viewMode === "month" ? monthShareTotal : courseShareTotal;
    doc.text(
      `Total teacher share: PKR ${total.toLocaleString()}`,
      marginX,
      cursorY,
    );
    cursorY += 8;

    const tableOpts = {
      startY: cursorY,
      margin: {
        top: tableTopMargin,
        left: marginX,
        right: marginX,
        bottom: PDF_BRAND_FOOTER_MARGIN,
      },
      didDrawPage: brandPageHook,
      headStyles: { fillColor: [213, 204, 253], textColor: [64, 59, 85] },
    };

    if (viewMode === "course") {
      const head = [
        "Student",
        "Email",
        "Fee Mode",
        ...courseMonths.map((period) => period.label),
      ];
      const body = matrixStudents.map((student) => [
        student.name || "—",
        student.email || "—",
        student.feemode || "—",
        ...courseMonths.map((period) => {
          const cell = student.periods[`${period.year}-${period.month}`];
          if (!cell) return "—";
          if (
            (cell.feePeriodStatus === "waived" ||
              cell.feePeriodStatus === "zero_due") &&
            teacherShareForRow(cell) <= 0
          ) {
            return feePeriodStatusLabel(cell.feePeriodStatus);
          }
          const share = teacherShareForRow(cell);
          const date = formatDisplayDate(cell.paymentDate);
          return `PKR ${share.toLocaleString()} (${date})`;
        }),
      ]);

      doc.autoTable({
        ...tableOpts,
        head: [head],
        body,
        styles: { fontSize: 8 },
      });

      doc.save(`teacher_${teacher.id}_course_${selectedCourse.id}.pdf`);
      return;
    }

    const tableColumn = [
      "Student",
      "Email",
      "Fee Mode",
      "Teacher Share",
      "Fee Status",
      "Payment Date",
    ];
    const tableRows = monthRows.map((row) => [
      row.name || "—",
      row.email || "—",
      row.feemode || "—",
      `PKR ${teacherShareForRow(row).toLocaleString()}`,
      feePeriodStatusLabel(row.feePeriodStatus),
      formatDisplayDate(row.paymentDate),
    ]);

    doc.autoTable({
      ...tableOpts,
      head: [tableColumn],
      body: tableRows,
    });

    const monthPad = String(appliedFilter.month).padStart(2, "0");
    doc.save(
      `teacher_${teacher.id}_${appliedFilter.courseId}_${appliedFilter.year}-${monthPad}.pdf`,
    );
  };

  const subtitleText = (() => {
    if (viewMode === "month" && appliedFilter) {
      return `Month view: ${appliedFilter.courseTitle} · ${appliedFilter.periodLabel}. Amounts are teacher share (commission ${teacher?.commission ?? 0}%).`;
    }
    if (viewMode === "course" && selectedCourse) {
      return `Course view: ${selectedCourse.title}. Amounts are teacher share (commission ${teacher?.commission ?? 0}%). Select a month and Apply for a single-month breakdown.`;
    }
    return "Select a course above to view student fee periods by month, or pick a month and Apply for a single-month breakdown.";
  })();

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  const renderEmptyPrompt = (title, detail) => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: "300px", padding: "40px" }}
    >
      <div
        style={{
          width: "46px",
          height: "46px",
          borderRadius: "12px",
          backgroundColor: "#f3f1f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "14px",
        }}
      >
        <i
          className="fa-solid fa-users"
          style={{ color: "#77718b", fontSize: "18px" }}
        />
      </div>
      <div
        style={{
          color: "#403b55",
          fontWeight: "600",
          fontSize: "14px",
          textAlign: "center",
        }}
      >
        {title}
      </div>
      <small
        style={{
          color: "#8a8498",
          marginTop: "6px",
          textAlign: "center",
          maxWidth: "420px",
          lineHeight: "1.5",
        }}
      >
        {detail}
      </small>
    </div>
  );

  return (
    <div className="mb-5">
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            height: "109vh",
            minHeight: 0,
            padding: "10px 50px 0",
            overflow: "hidden",
          }}
        >
          {teacher && (
            <>
                <div style={{ flexShrink: 0 }}>
                  <div style={{ padding: "18px 20px" }}>
                    <div className="d-flex justify-content-between">
                      <div className="d-flex align-items-center">
                        <AppTooltip label="Back to teachers">
                        <button
                          className="btn"
                          onClick={() => navigate("/admin/teachers")}
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: 0,
                            marginRight: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#d5d1fd",
                            border: "none",
                            borderRadius: "8px",
                            color: "#403b55",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faArrowLeft}
                            style={{ fontSize: "13px" }}
                          />
                        </button>
                        </AppTooltip>
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#625d75",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.6px",
                              marginBottom: "2px",
                            }}
                          >
                            Teacher
                          </div>
                          <h5
                            className="mb-0"
                            style={{
                              color: "#302d45",
                              fontWeight: "700",
                              fontSize: "20px",
                            }}
                          >
                            {teacher.name}
                          </h5>
                        </div>
                      </div>
                      <button
                        className="btn"
                        onClick={() =>
                          navigate(`/admin/teachers/${id}/account`)
                        }
                        style={{
                          height: "38px",
                          display: "flex",
                          alignItems: "center",
                          color: "#403b55",
                          border: "none",
                          padding: "0 15px",
                          borderRadius: "8px",
                          fontWeight: "600",
                          fontSize: "13px",
                          textDecoration: "none",
                          whiteSpace: "nowrap",
                          backgroundColor: "#bff1db",
                        }}
                      >
                        View Account
                      </button>
                    </div>
                  </div>

                <div
                  style={{
                    backgroundColor: "#d5d1fd",
                    borderRadius: "16px",
                    padding: "20px 22px",
                    marginBottom: "16px",
                    flexShrink: 0,
                  }}
                >
                  <div
                    className="d-flex justify-content-between align-items-center"
                    style={{ marginBottom: "14px" }}
                  >
                    <div>
                      <h4
                        className="mb-1"
                        style={{ fontWeight: "700", color: "#302d45" }}
                      >
                        Course Registrations
                      </h4>
                      <small style={{ color: "#625d75", fontSize: "12px" }}>
                        {subtitleText}
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <button
                        className="btn"
                        onClick={handleToggleActiveCourses}
                        style={controlBtnStyle}
                      >
                        {showActiveCoursesOnly
                          ? "Show All Courses"
                          : "Show Active Courses Only"}
                      </button>
                    </div>
                  </div>

                  {listedCourses.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "7px",
                      }}
                    >
                      {listedCourses.map((course) => {
                        const isSelected = selectedCourse?.id === course.id;
                        return (
                          <button
                            key={course.id}
                            className="btn"
                            onClick={() => handleSelectCourse(course)}
                            style={{
                              backgroundColor: isSelected ? "#e5edf4" : "#fff",
                              color: isSelected ? "#435466" : "#625d75",
                              border: isSelected
                                ? "1px solid #ccdbe8"
                                : "none",
                              borderBottom: isSelected
                                ? "3px solid #41a2f7"
                                : "none",
                              borderRadius: "8px",
                              padding: "8px 12px",
                              fontSize: "12px",
                              fontWeight: "600",
                              transition: "all 0.2s ease",
                            }}
                          >
                            {course.title}
                            <span
                              style={{
                                marginLeft: "7px",
                                opacity: isSelected ? 0.8 : 0.65,
                                fontSize: "10px",
                              }}
                            >
                              PKR {course.fee || 0}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <small style={{ color: "#625d75" }}>
                      No courses available for this teacher.
                    </small>
                  )}

                  {selectedCourse && (
                    <div className="mt-3">
                      <label
                        className="form-label mb-1"
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#514b65",
                          letterSpacing: "0.2px",
                        }}
                      >
                        MONTH FOR {selectedCourse.title.toUpperCase()}
                      </label>
                      {courseMonths.length === 0 ? (
                        <small style={{ color: "#625d75", display: "block" }}>
                          This course has no session months configured.
                        </small>
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-between gap-2 flex-wrap"
                        >
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "7px",
                              alignItems: "center",
                            }}
                          >
                            {courseMonths.map((period) => {
                              const isSelected =
                                selectedMonth?.month === period.month &&
                                selectedMonth?.year === period.year;
                              return (
                                <button
                                  key={`${period.year}-${period.month}`}
                                  type="button"
                                  className="btn"
                                  onClick={() => setSelectedMonth(period)}
                                  style={{
                                    backgroundColor: isSelected
                                      ? "#e5edf4"
                                      : "#fff",
                                    color: isSelected ? "#435466" : "#625d75",
                                    border: isSelected
                                      ? "1px solid #ccdbe8"
                                      : "none",
                                    borderBottom: isSelected
                                      ? "3px solid #41a2f7"
                                      : "none",
                                    borderRadius: "8px",
                                    padding: "8px 12px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  {period.label}
                                </button>
                              );
                            })}
                            <button
                              className="btn"
                              disabled={!selectedCourse || !selectedMonth}
                              onClick={handleApplyFilter}
                              style={{
                                height: "38px",
                                backgroundColor:
                                  !selectedCourse || !selectedMonth
                                    ? "#f3f1f7"
                                    : "#fff",
                                color:
                                  !selectedCourse || !selectedMonth
                                    ? "#aaa5b5"
                                    : "#514637",
                                border: "none",
                                fontWeight: "600",
                                fontSize: "12px",
                                padding: "0 15px",
                                borderRadius: "8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Apply Filter
                            </button>
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <button
                              className="btn"
                              onClick={handleClearFilter}
                              style={controlBtnStyle}
                            >
                              Clear
                            </button>
                            {canExport && (
                              <button
                                className="btn"
                                onClick={downloadPDF}
                                style={controlBtnStyle}
                              >
                                Export PDF
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </div>

                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      borderRadius: "14px",
                      border: "1px solid #eeeaf8",
                      boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                    }}
                  >
                    {loadingSheet ? (
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "100%", minHeight: "300px" }}
                      >
                        <Loader />
                      </div>
                    ) : viewMode === "empty" ? (
                      renderEmptyPrompt(
                        "Select a course to get started",
                        "Choose a course above to see student registrations by month. Or select a course, pick a month, and Apply for a single-month breakdown. Amounts shown are teacher share after commission.",
                      )
                    ) : viewMode === "course" && matrixStudents.length === 0 ? (
                      renderEmptyPrompt(
                        "No settled registrations for this course",
                        "No paid or FOC registrations were found for the selected course.",
                      )
                    ) : viewMode === "month" && monthRows.length === 0 ? (
                      renderEmptyPrompt(
                        "No registrations found for this course and month",
                        "Try applying a different month filter.",
                      )
                    ) : viewMode === "course" ? (
                      <div
                        style={{
                          height: "100%",
                          overflowX: "auto",
                          overflowY: "auto",
                        }}
                      >
                        <table
                          className="table align-middle mb-0"
                          style={{
                            width: "100%",
                            minWidth: `${520 + courseMonths.length * 120}px`,
                            background: "#fff",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                          }}
                        >
                          <thead>
                            <tr>
                              <th style={thStyle}>STUDENT DETAILS</th>
                              <th style={thStyle}>FEE MODE</th>
                              {courseMonths.map((period) => (
                                <th
                                  key={`${period.year}-${period.month}`}
                                  style={thStyle}
                                >
                                  {period.label.toUpperCase()}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedRows.map((student) => (
                              <tr key={student.registrationId}>
                                <td
                                  style={{
                                    ...tdStyle,
                                    lineHeight: "1.7",
                                  }}
                                >
                                  <div style={{ fontWeight: "600" }}>
                                    {student.name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#8a8498",
                                    }}
                                  >
                                    {student.email}
                                  </div>
                                </td>
                                <td style={tdStyle}>
                                  <div>{student.feemode || "—"}</div>
                                  {Number(student.discount) > 0 && (
                                    <span
                                      style={{
                                        display: "inline-block",
                                        marginTop: "4px",
                                        padding: "3px 7px",
                                        borderRadius: "6px",
                                        backgroundColor: "#fff0f0",
                                        color: "#dc3545",
                                        fontSize: "10px",
                                        fontWeight: "600",
                                      }}
                                    >
                                      PKR {student.discount} off
                                    </span>
                                  )}
                                </td>
                                {courseMonths.map((period) => (
                                  <td
                                    key={`${student.registrationId}-${period.year}-${period.month}`}
                                    style={tdStyle}
                                  >
                                    {renderMonthCell(
                                      student.periods[
                                        `${period.year}-${period.month}`
                                      ],
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div
                        style={{
                          height: "100%",
                          overflowX: "auto",
                          overflowY: "auto",
                        }}
                      >
                        <table
                          className="table align-middle mb-0"
                          style={{
                            width: "100%",
                            minWidth: "860px",
                            background: "#fff",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                          }}
                        >
                          <thead>
                            <tr>
                              {[
                                "Student Details",
                                "Fee Mode",
                                "Teacher Share",
                                "Fee Status",
                                "Payment Date",
                              ].map((key) => (
                                <th key={key} style={thStyle}>
                                  {key.toUpperCase()}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedRows.map((row) => {
                              const teacherShare = teacherShareForRow(row);
                              return (
                                <tr
                                  key={`${row.feePeriodId}-${row.registrationId}`}
                                >
                                  <td
                                    style={{
                                      ...tdStyle,
                                      lineHeight: "1.7",
                                    }}
                                  >
                                    <div style={{ fontWeight: "600" }}>
                                      {row.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#8a8498",
                                      }}
                                    >
                                      {row.email}
                                    </div>
                                  </td>
                                  <td style={tdStyle}>
                                    <div>{row.feemode || "—"}</div>
                                    {Number(row.discount) > 0 && (
                                      <span
                                        style={{
                                          display: "inline-block",
                                          marginTop: "4px",
                                          padding: "3px 7px",
                                          borderRadius: "6px",
                                          backgroundColor: "#fff0f0",
                                          color: "#dc3545",
                                          fontSize: "10px",
                                          fontWeight: "600",
                                        }}
                                      >
                                        PKR {row.discount} off
                                      </span>
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyle,
                                      fontWeight: "600",
                                    }}
                                  >
                                    PKR {teacherShare.toLocaleString()}
                                  </td>
                                  <td style={tdStyle}>
                                    <span
                                      style={{
                                        display: "inline-block",
                                        padding: "5px 9px",
                                        borderRadius: "7px",
                                        backgroundColor:
                                          row.feePeriodStatus === "waived"
                                            ? "#f9e1be"
                                            : row.feePeriodStatus === "zero_due"
                                              ? "#f3f1f7"
                                              : "#eefaf4",
                                        color:
                                          row.feePeriodStatus === "waived"
                                            ? "#8a5a00"
                                            : row.feePeriodStatus === "zero_due"
                                              ? "#625d75"
                                              : "#355c4a",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {feePeriodStatusLabel(row.feePeriodStatus)}
                                    </span>
                                  </td>
                                  <td
                                    style={{
                                      ...tdStyle,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {formatDisplayDate(row.paymentDate)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {tableRows.length > 0 && (
                    <PaginationFooter
                      page={currentPage}
                      limit={rowsPerPage}
                      total={tableRows.length}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      itemLabel={rowLabel}
                    />
                  )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default TeacherDetails;
